import React, { useState, useEffect, useRef } from 'react';
import { fabric } from 'fabric';
import html2canvas from 'html2canvas';

const TEMPLATE_DIMENSIONS = {
  width: 200,
  height: 200
};

const TEMPLATES = {
  'T-Shirts': {
    front: 'https://highline-apparel.com/images/templates/t-shirts-front.png',
    back: 'https://highline-apparel.com/images/templates/t-shirts-back.png'
  },
  'Hoodies': {
    front: 'https://highline-apparel.com/images/templates/hoodies-front.png',
    back: 'https://highline-apparel.com/images/templates/hoodies-back.png'
  }
};

const ProductPreview = ({ 
  color = '#FFFFFF', 
  artwork,
  productType = 'T-Shirts',
  view = 'front',
  onSaveSnapshot = () => {}
}) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isSaved, setIsSaved] = useState(false);
  const canvasRef = useRef(null);
  const fabricCanvasRef = useRef(null);
  const containerRef = useRef(null);
  const templateRef = useRef(null);

  useEffect(() => {
    const initCanvas = async () => {
      try {
        if (productType === 'Hats') {
          setLoading(false);
          return;
        }

        const templateUrl = TEMPLATES[productType]?.[view];
        if (!templateUrl) {
          throw new Error(`No template available for ${productType} ${view} view`);
        }

        const img = new Image();
        await new Promise((resolve, reject) => {
          img.onload = resolve;
          img.onerror = reject;
          img.src = templateUrl;
        });

        if (!canvasRef.current) return;

        if (!fabricCanvasRef.current) {
          fabricCanvasRef.current = new fabric.Canvas(canvasRef.current, {
            width: TEMPLATE_DIMENSIONS.width,
            height: TEMPLATE_DIMENSIONS.height,
            backgroundColor: color,
            preserveObjectStacking: true
          });

          fabricCanvasRef.current.on('object:modified', () => {
            setIsSaved(false);
          });
        } else {
          fabricCanvasRef.current.clear();
          fabricCanvasRef.current.setBackgroundColor(color);
        }

        const scale = Math.min(
          TEMPLATE_DIMENSIONS.width / img.width,
          TEMPLATE_DIMENSIONS.height / img.height
        );

        const fabricImg = new fabric.Image(img, {
          left: (TEMPLATE_DIMENSIONS.width - (img.width * scale)) / 2,
          top: (TEMPLATE_DIMENSIONS.height - (img.height * scale)) / 2,
          scaleX: scale,
          scaleY: scale,
          selectable: false,
          evented: false
        });

        templateRef.current = fabricImg;
        fabricCanvasRef.current.add(fabricImg);

        if (artwork) {
          addArtwork(artwork);
        }

        fabricCanvasRef.current.renderAll();
        setLoading(false);
      } catch (err) {
        console.error('Error initializing canvas:', err);
        setError('Failed to load preview');
        setLoading(false);
      }
    };

    initCanvas();
    setIsSaved(false);

    return () => {
      if (fabricCanvasRef.current) {
        fabricCanvasRef.current.dispose();
        fabricCanvasRef.current = null;
      }
      templateRef.current = null;
    };
  }, [color, productType, view]);

  useEffect(() => {
    if (fabricCanvasRef.current && artwork) {
      addArtwork(artwork);
    }
  }, [artwork]);

  const addArtwork = (artworkUrl) => {
    if (!fabricCanvasRef.current) return;

    const existingArtwork = fabricCanvasRef.current.getObjects().find(obj => obj.type === 'artwork');
    if (existingArtwork) {
      fabricCanvasRef.current.remove(existingArtwork);
    }

    fabric.Image.fromURL(artworkUrl, (img) => {
      const canvasWidth = fabricCanvasRef.current.width;
      const canvasHeight = fabricCanvasRef.current.height;

      const maxWidth = canvasWidth * 0.5;
      const maxHeight = canvasHeight * 0.5;
      const scale = Math.min(maxWidth / img.width, maxHeight / img.height);

      img.set({
        left: (canvasWidth - img.width * scale) / 2,
        top: (canvasHeight - img.height * scale) / 2,
        scaleX: scale,
        scaleY: scale,
        type: 'artwork',
        cornerColor: '#00ffff',
        cornerStrokeColor: '#000000',
        cornerStyle: 'circle',
        transparentCorners: false,
        padding: 10
      });

      fabricCanvasRef.current.add(img);
      fabricCanvasRef.current.setActiveObject(img);
      
      if (templateRef.current) {
        fabricCanvasRef.current.bringToFront(templateRef.current);
      }
      
      fabricCanvasRef.current.renderAll();
      setIsSaved(false);
    });
  };

  const handleSaveSnapshot = async () => {
    if (!containerRef.current) return;

    try {
      const canvas = await html2canvas(containerRef.current, {
        backgroundColor: null,
        scale: 2,
        logging: false,
        useCORS: true
      });

      const snapshot = canvas.toDataURL('image/png');
      onSaveSnapshot(snapshot);
      setIsSaved(true);
    } catch (err) {
      console.error('Error capturing snapshot:', err);
      setError('Failed to save preview');
    }
  };

  if (productType === 'Hats') {
    return (
      <div className="bg-gray-800 p-2 rounded-lg">
        <p className="text-sm text-gray-400 text-center">
          Upload your hat embroidery artwork. We'll ensure proper placement and sizing.
        </p>
      </div>
    );
  }

  return (
    <div className="relative w-full max-w-[200px] mx-auto">
      <div className="bg-gray-800 p-1 rounded-lg">
        <div ref={containerRef} className="relative w-full h-[200px]">
          {loading && (
            <div className="absolute inset-0 flex items-center justify-center bg-gray-800">
              <p className="text-gray-400">Loading preview...</p>
            </div>
          )}
          <canvas
            ref={canvasRef}
            className="w-full h-full"
          />
        </div>

        {error && (
          <p className="mt-0.5 text-xs text-red-500 text-center">
            {error}
          </p>
        )}

        {artwork && !error && (
          <div className="mt-0.5 space-y-0.5">
            <p className="text-xs text-gray-400 text-center">
              Click and drag to move artwork. Use corners to resize.
            </p>
            <button
              type="button"
              onClick={handleSaveSnapshot}
              className={`w-full px-2 py-1 rounded text-xs transition-colors ${
                isSaved 
                  ? 'bg-green-500 hover:bg-green-600 text-black'
                  : 'bg-cyan-500 hover:bg-cyan-600 text-black'
              }`}
            >
              {isSaved ? 'Preview Saved!' : 'Save Preview'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default ProductPreview;